import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Badge } from '@components';
import { useDelayedLoading } from '@hooks/useDelayedLoading';
import { Document } from '../types';

interface DocumentsTableProps {
  documents: Document[];
  onDownload: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onUploadDocument?: () => void;
  loading?: boolean;
}

export const DocumentsTable: React.FC<DocumentsTableProps> = ({
  documents,
  onDownload,
  onEdit,
  onDelete,
  onUploadDocument,
  loading = false,
}) => {
  const { t } = useTranslation();
  const showDelayedLoading = useDelayedLoading(loading, { delay: 500 });
  const getStatusBadge = (status: Document['status']) => {
    switch (status) {
      case 'processed':
        return (
          <Badge variant="success" size="sm">
            {t('knowledgeBase.processed')}
          </Badge>
        );
      case 'processing':
        return (
          <Badge variant="warning" size="sm">
            {t('knowledgeBase.processing')}
          </Badge>
        );
      case 'uploaded':
        return (
          <Badge variant="info" size="sm">
            {t('knowledgeBase.uploaded')}
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="error" size="sm">
            {t('knowledgeBase.failed')}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" size="sm">
            {status}
          </Badge>
        );
    }
  };

  const getKnowledgeBaseBadge = (kb: string) => {
    switch (kb) {
      case 'General':
        return (
          <Badge variant="info" size="sm">
            General
          </Badge>
        );
      case 'Technical':
        return (
          <Badge variant="warning" size="sm">
            Technical
          </Badge>
        );
      case 'Support':
        return (
          <Badge variant="info" size="sm">
            Support
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" size="sm">
            {kb}
          </Badge>
        );
    }
  };

  return (
    <Card>
      <CardContent>
        <div className="relative">
          {showDelayedLoading && (
            <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                <span>{t('knowledgeBase.loadingDocuments')}</span>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('knowledgeBase.document')}
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('knowledgeBase.knowledgeBaseName')}
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('common.status')}
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('knowledgeBase.uploaded')}
                  </th>
                  <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {documents.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12">
                      <div className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                          <span className="material-icons text-2xl text-gray-400 dark:text-gray-500">
                            description
                          </span>
                        </div>
                        <div className="space-y-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {t('knowledgeBase.noDocumentsFound')}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                            {t('knowledgeBase.uploadDocumentsDescription')}
                          </p>
                        </div>
                        <div className="pt-2">
                          <Button
                            variant="gradient-multi"
                            size="sm"
                            onClick={onUploadDocument}
                            className="inline-flex items-center gap-2"
                          >
                            <span className="material-icons text-sm">upload</span>
                            {t('knowledgeBase.uploadDocument')}
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  documents.map(document => (
                    <tr
                      key={document.id}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <span className="material-icons text-blue-600 dark:text-blue-400 text-sm">
                              description
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {document.title}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {document.fileSizeVerbose}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {getKnowledgeBaseBadge(document.knowledgeBase.name)}
                      </td>
                      <td className="py-2 px-3">{getStatusBadge(document.status)}</td>
                      <td className="py-2 px-3 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(document.created).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDownload(document.id)}
                            className="p-1 h-8 w-8"
                            title="Download Document"
                          >
                            <span className="material-icons text-lg">download</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEdit(document.id)}
                            className="p-1 h-8 w-8"
                            title="Edit Document"
                          >
                            <span className="material-icons text-lg">edit</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(document.id)}
                            className="p-1 h-8 w-8"
                            title="Delete Document"
                          >
                            <span className="material-icons text-red-600 text-lg">delete</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
